import Input from "Components/Form/Input";
import { Field, Form, useFormikContext } from "formik";
import { useState, useEffect } from "react";
import { usePreOrderDetails } from "Pages/PreBudgetConvertionIA/hooks/usePreOrderDetails";
import { useParams } from "react-router";
import { Edit, Check } from "lucide-react";
import { Box } from "styles/globalComponents";
import { Button } from "Components/Button";
import { InputPhone } from "Components/Form/PhoneInput";
import * as yup from "yup";
import { isValidPhoneMask } from "Utils/Phone";
import { MaskCPF } from "Utils/Masks";
import { isCPF } from "Services/schema/validations";
import { formatPhoneNumberIntl } from "react-phone-number-input";

// Função de validação de nome reutilizável
const nameValidation = yup
  .string()
  .matches(
    /^[\p{L}\s\-'.,]+$/u,
    "Nome deve conter apenas letras, espaços, hífens, aspas e pontos"
  );

export type PersonInfo = {
  full_name: string;
  email: string;
  phone: string;
  doctor: string;
  crm: string;
  document: string;
  mothers_full_name: string;
};

const validatePhone = (phone: string) => {
  const [ddi, ddd, n1, n2] = formatPhoneNumberIntl(phone).split(" ");

  if (!ddi || !ddd || !n1 || !n2) return false;

  return true;
};

export const personData = yup.object({
  name: nameValidation.required("Nome do paciente é obrigatório"),
  document: yup
    .string()
    .test("document", "CPF inválido", isCPF)
    .required("Documento é obrigatório"),
  doctor: nameValidation
    .nullable()
    .test(
      "doctor-validation",
      "Nome do médico deve ser válido",
      function (value) {
        if (!value || value.trim() === "") return true; // Campo vazio é válido
        return nameValidation.isValidSync(value);
      }
    ),
  crm: yup.string().nullable(),
  mothers_full_name: nameValidation
    .nullable()
    .test("mother-validation", "Nome da mãe deve ser válido", function (value) {
      if (!value || value.trim() === "") return true; // Campo vazio é válido
      return nameValidation.isValidSync(value);
    }),
  phone: yup
    .string()
    .required("Telefone é obrigatório")
    .test("phone", "Telefone inválido", (value) => {
      if (!value) return false;

      try {
        // Tentar validar com a função padrão primeiro

        const validPhone = validatePhone(value);

        if (!validPhone) return false;

        const isValid = isValidPhoneMask(value);
        if (isValid) return true;

        // Se falhar, tentar formatar e validar novamente
        // Para telefones brasileiros: +55 11 99999-9999
        let cleanValue = value
          .replace(/[^\d+\s+]/g, "") // Manter apenas dígitos, espaços e +
          .replace(/\s+/g, " ") // Normalizar espaços
          .trim();

        // Garantir que tenha o formato +55 11 999999999
        if (cleanValue.startsWith("+")) {
          // Remover hífens e formatar corretamente
          cleanValue = cleanValue.replace(/-/g, "");

          // Validar novamente
          return isValidPhoneMask(cleanValue);
        }

        // Se não tem o +, tentar adicionar e validar
        if (cleanValue.length >= 10) {
          const withPlus = `+${cleanValue}`;
          return isValidPhoneMask(withPlus);
        }

        return false;
      } catch (error) {
        console.warn("Erro na validação do telefone:", error);
        return false;
      }
    }),
});

export type Values = yup.InferType<typeof personData>;

interface InfoDetailsProps {
  blockUserInteraction: boolean;
  forceEditMode: boolean;
  onEditModeChange: (editMode: boolean) => void;
  iaData?: {
    requestingDoctor?: string;
    doctorCRM?: string;
  };
}

export const InfoDetails = ({
  blockUserInteraction,
  forceEditMode,
  onEditModeChange,
  iaData,
}: InfoDetailsProps) => {
  const [editFields, setEditFields] = useState(false);

  const { id } = useParams<{ id: string }>();

  const preOderDetailsAPI = usePreOrderDetails(Number(id));

  let dateSchedule = "Não informado";
  let timeSchedule = "Não informado";
  let patientPhone = "";
  let zipcode = "";

  const {
    full_name,
    client,
    document,
    address,
    appointment_at,
    ddi,
    ddd,
    phone,
    doctor,
    crm,
    email,
    gender,
  } = preOderDetailsAPI.data || {};

  let customDdi = "55";

  if (ddi) {
    customDdi = ddi.replace(/\D/g, "");
  }

  if (appointment_at) {
    const [date, time] = appointment_at?.split(" ") || [];
    const [yyyy, mm, dd] = date?.split("-");
    timeSchedule = `${time?.slice(0, 5)} hrs`;
    dateSchedule = `${dd}/${mm}/${yyyy}`;
  }

  if (phone) {
    // O telefone vem da API como 5511999999999 (sem o +)
    // Precisamos formatar para +55 11 99999-9999 para a validação funcionar

    // Se já tem o +, usar como está
    if (phone.startsWith("+")) {
      patientPhone = phone;
    } else {
      // Se não tem o +, adicionar e formatar
      const ddiClean = customDdi;
      const dddClean = ddd?.replace(/[^\d]/g, "");
      const phoneClean = phone?.replace(/[^\d]/g, "");
      const slickedPhone = `${phoneClean?.slice(0, -4)}-${phoneClean?.slice(
        -4
      )}`;

      if (ddiClean && dddClean && phoneClean) {
        patientPhone = `+${ddiClean} ${dddClean} ${slickedPhone}`;
      }
    }
  }

  if (address?.zipcode) {
    const zip = address.zipcode.replaceAll("-", "");
    zipcode = `${zip.slice(0, 5)}-${zip.slice(5, 8)}`;
  }

  const preOrderaddressDetails = [
    { name: "Rua", value: [address?.street || ""] },
    {
      name: "Número",
      value: [address?.number || ""],
    },
    { name: "Bairro", value: [address?.neighborhood || ""] },
    { name: "Complemento", value: [address?.complement || ""] },
    { name: "CEP", value: [zipcode] },
    { name: "Cidade", value: [address?.city || ""] },
    { name: "Estado", value: [address?.state || ""] },
  ];

  const formik = useFormikContext<Values>();

  const getGenders = (patientGender?: string) => {
    switch (patientGender?.toLowerCase()) {
      case "m":
        return "Masculino";
      case "f":
        return "Feminino";
      default:
        return "Indefinido";
    }
  };

  useEffect(() => {
    formik.setFieldValue("phone", patientPhone);
  }, [patientPhone]);

  // Sincronizar o telefone da API com o formulário quando disponível
  useEffect(() => {
    if (formik && patientPhone && !formik.values.phone) {
      // Garantir que o telefone esteja no formato correto antes de definir
      const formattedPhone = patientPhone.trim();

      if (formattedPhone) {
        // Definir o valor do campo
        formik.setFieldValue("phone", formattedPhone);

        // Aguardar um pouco para garantir que o valor foi definido
        setTimeout(() => {
          // Marcar o campo como "touched" para habilitar a validação
          formik.setFieldTouched("phone", true);

          // Forçar uma validação do campo após marcar como touched
          setTimeout(() => {
            formik.validateField("phone");
          }, 50);
        }, 100);
      }
    }
  }, [patientPhone, formik?.values.phone]);

  // Sincronizar o estado local com o estado do pai (modo de edição forçado)
  useEffect(() => {
    if (forceEditMode) {
      setEditFields(true);
    }
  }, [forceEditMode, formik]);

  // Resetar modo forçado quando o usuário sair do modo de edição manualmente
  useEffect(() => {
    if (!editFields && forceEditMode) {
      onEditModeChange(false);
    }
  }, [editFields, forceEditMode, onEditModeChange]);

  // Preencher automaticamente os campos do médico com dados da IA
  useEffect(() => {
    if (iaData && formik) {
      const updates: Partial<Values> = {};

      if (iaData.requestingDoctor && !formik.values.doctor) {
        updates.doctor = iaData.requestingDoctor;
      }

      if (iaData.doctorCRM && !formik.values.crm) {
        updates.crm = iaData.doctorCRM;
      }

      if (Object.keys(updates).length > 0) {
        formik.setValues((prev) => ({
          ...prev,
          ...updates,
        }));
      }
    }
  }, [iaData, formik]);

  return (
    <Form id="teste">
      <div
        className="tab-item patient-info"
        style={{ display: "flex", flexDirection: "column", gap: "30px" }}
      >
        <h5
          className="text-center"
          style={{
            color: "var(--color-purple)",
            fontSize: "24px",
            lineHeight: "32px",
            marginBottom: "20px",
          }}
        >
          Dados de agendamento
        </h5>

        <div
          className="patient"
          style={{ display: "flex", flexDirection: "column", gap: "5px" }}
        >
          <Box display="flex" justify="space-between" align="center">
            <h3
              style={{
                color: "var(--color-purple)",
                fontSize: "1.2rem",
                marginBottom: "0",
              }}
            >
              Dados do paciente
            </h3>

            {editFields ? (
              <Button
                classButton="edit-btn"
                onClick={() => setEditFields(false)}
                disabled={formik.isSubmitting || blockUserInteraction}
              >
                <Check size={15} />
              </Button>
            ) : (
              <Button
                classButton="edit-btn"
                onClick={() => setEditFields(!editFields)}
                disabled={blockUserInteraction}
              >
                <Edit size={15} />
              </Button>
            )}
          </Box>

          <div
            className="item"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "5px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <span>
                <strong>Nome:</strong>{" "}
              </span>

              {editFields ? (
                <Field
                  id="name"
                  type="text"
                  onChange={formik.handleChange}
                  value={formik.values.name}
                  component={Input}
                  className="input _opacity-gray"
                  error={formik.touched.name && formik.errors.name}
                />
              ) : (
                <span style={{ fontSize: "0.9rem" }}>{formik.values.name}</span>
              )}
            </div>
          </div>

          <div
            className="item"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "5px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <span>
                <strong>Documento:</strong>{" "}
              </span>

              {editFields ? (
                <Field
                  id="document"
                  type="text"
                  value={formik.values.document}
                  onChange={formik.handleChange}
                  component={Input}
                  className="input _opacity-gray"
                  error={formik.touched.document && formik.errors.document}
                />
              ) : (
                <span style={{ fontSize: "0.9rem" }}>
                  {MaskCPF(formik.values.document)}
                </span>
              )}
            </div>
          </div>

          <div
            className="item"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "5px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <span>
                <strong>Telefone: </strong>
              </span>

              {editFields ? (
                <Field
                  id="phone"
                  type="text"
                  value={formik.values.phone}
                  onChange={(phone: string) =>
                    formik.setFieldValue("phone", phone)
                  }
                  onBlur={() => {
                    // Garantir que o campo seja marcado como touched
                    if (!formik.touched.phone) {
                      formik.setFieldTouched("phone", true);
                    }
                  }}
                  component={InputPhone}
                  className="input _opacity-gray"
                  error={formik.touched.phone && formik.errors.phone}
                />
              ) : (
                <span style={{ fontSize: "0.9rem" }}>
                  {formik.values.phone}
                </span>
              )}
            </div>
          </div>

          <div
            className="item"
            style={{ display: "flex", alignItems: "center", gap: "5px" }}
          >
            <span>
              <strong>Convênio: </strong>
            </span>
            <span style={{ fontSize: "0.9rem" }}>{client?.name}</span>
          </div>

          <div
            className="item"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "5px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <span>
                <strong>Médico: </strong>
              </span>

              {editFields ? (
                <Field
                  id="doctor"
                  type="text"
                  value={formik.values.doctor}
                  onChange={formik.handleChange}
                  component={Input}
                  className="input _opacity-gray"
                  error={formik.touched.doctor && formik.errors.doctor}
                />
              ) : (
                <span style={{ fontSize: "0.9rem" }}>
                  {formik.values.doctor}
                </span>
              )}
            </div>
          </div>

          <div
            className="item"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "5px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <span>
                <strong>CRM: </strong>
              </span>

              {editFields ? (
                <Field
                  id="crm"
                  type="text"
                  value={formik.values.crm}
                  onChange={formik.handleChange}
                  component={Input}
                  className="input _opacity-gray"
                  error={formik.touched.crm && formik.errors.crm}
                />
              ) : (
                <span style={{ fontSize: "0.9rem" }}>{formik.values.crm}</span>
              )}
            </div>
          </div>

          <div
            className="item"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "5px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <span>
                <strong>Nome da Mãe: </strong>
              </span>

              {editFields ? (
                <Field
                  id="mothers_full_name"
                  type="text"
                  value={formik.values.mothers_full_name}
                  onChange={formik.handleChange}
                  component={Input}
                  className="input _opacity-gray"
                  error={
                    formik.touched.mothers_full_name &&
                    formik.errors.mothers_full_name
                  }
                />
              ) : (
                <span style={{ fontSize: "0.9rem" }}>
                  {formik.values.mothers_full_name || "Não informado"}
                </span>
              )}
            </div>
          </div>

          <div className="item">
            <span>
              <strong>Gênero biológico: </strong>
            </span>

            <span>{getGenders(gender)}</span>
          </div>
        </div>

        <div
          className="schedule"
          style={{ display: "flex", flexDirection: "column", gap: "5px" }}
        >
          <h3
            style={{
              color: "var(--color-purple)",
              fontSize: "1.2rem",
              marginBottom: "0",
            }}
          >
            Dados do agendamento
          </h3>

          <div
            className="item"
            style={{ display: "flex", alignItems: "center", gap: "5px" }}
          >
            <span>
              <strong>Dia:</strong>{" "}
            </span>
            <span style={{ fontSize: "0.9rem" }}>{dateSchedule}</span>
          </div>

          <div
            className="item"
            style={{ display: "flex", alignItems: "center", gap: "5px" }}
          >
            <span>
              <strong>Horário:</strong>{" "}
            </span>
            <span style={{ fontSize: "0.9rem" }}>{timeSchedule}</span>
          </div>
        </div>

        <div
          className="address"
          style={{ display: "flex", flexDirection: "column", gap: "5px" }}
        >
          <h3
            style={{
              color: "var(--color-purple)",
              fontSize: "1.2rem",
              marginBottom: "0",
            }}
          >
            Dados de endereço
          </h3>

          {preOrderaddressDetails.map((item, index) => (
            <div
              key={index}
              className="item"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <span>
                <strong>{item.name}:</strong>{" "}
              </span>
              <span style={{ fontSize: "0.9rem" }}>{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    </Form>
  );
};
